import React, { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import { FilterDeepContext } from '@opm/components/FilterCommon/state';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import type {
  DateShortcutType,
  ShortcutOptionType,
} from '@home/components/RangePicker/_config';
import { isEqual } from 'lodash-es';

const ALL_OF_TIME = 'All of time';

interface IMemeTrendFilterProps {
  filterValue: any;
  change: (value: { start_time: string; end_time: string }) => void;
  instanceId: string;
  startOfTime: string;
  endOfTime?: string;
}

function replaceAllOfTimeShortcut(
  list: ShortcutOptionType,
  startOfTime: string,
  endOfTime?: string,
): ShortcutOptionType {
  if (!endOfTime) return list;

  const replaceItem = (item: DateShortcutType): DateShortcutType => {
    if (item.name !== ALL_OF_TIME) return item;
    return {
      ...item,
      onClick: () => [moment(startOfTime), moment(endOfTime)],
    };
  };

  return Object.keys(list).reduce<ShortcutOptionType>((acc, key) => {
    acc[key] = (list[key] || []).map(replaceItem);
    return acc;
  }, {});
}

export default function MemeTrendFilter({
  filterValue,
  change,
  instanceId,
  startOfTime,
  endOfTime,
}: IMemeTrendFilterProps) {
  const { filter, setFilter, ...filterProps } = useFilterCommon({
    keys: ['MultiDateRange', 'RegionLanguage'],
    instanceId,
  });

  const { backCondConfig } = useContext(FilterDeepContext);

  const setShortcutOption = useCallback(
    (list: ShortcutOptionType) =>
      replaceAllOfTimeShortcut(list, startOfTime, endOfTime),
    [startOfTime, endOfTime],
  );

  const userDefaultValue = useMemo(() => {
    return {
      ...filterValue,
      start_time: startOfTime,
      end_time: endOfTime
    };
  }, [filterValue]);

  return (
    <div className="dbom-meme-trend-filter">
      <FilterCommon
        {...filterProps}
        realTimeMode
        userDefaultValue={userDefaultValue}
        createShortId={false}
        saveStorage={false}
        filterConfigProps={{
          MultiDateRange: { startOfTime, setShortcutOption },
        }}
        confirm={(value: any) => {
          if (!isEqual(value, filter)) {
            setFilter(value);
            change(value);
          }
        }}
        backCondConfig={backCondConfig}
        finallyDealwithDefaultValue={(value) => {
          return {
            ...value,
            ...userDefaultValue,
          };
        }}
      />
    </div>
  );
}
